import React from "react";
import {
	ArrayPick,
	Button,
	ClassNameProps,
	Column,
	If,
	makeClassName,
	Modal,
	Row,
	StyleProps,
} from "@hex-insights/core";
import { Badge } from "../../../../Components/Badge";
import { Icon } from "../../../../Components/Icon";
import { TextBlockLoadingPlaceholder, TextLineLoadingPlaceholder } from "../../../../Components/LoadingPlaceholder";
import { Announcement, Post, User, UserNameAndImageFragment } from "../../../../Utilities";
import { MediaAttachmentUtilities } from "../../../Utilities";
import * as ModelComponents from "../../_General";
import { AuthorTimestampView } from "../../_General/Author";
import * as MediaAttachmentComponents from "../../MediaAttachment";
import { SimpleSharingView, SimpleSharingViewProps } from "../../SharingRecord";
import * as SocialReactionComponents from "../../SocialReaction";
import { AnnouncementModal } from "../Modal";
import styles from "./styles.module.css";

export type AnnouncementInTimelineItem = Pick<Announcement, "id" | "title" | "body" | "startTime" | "endTime"> & {
	author: (Pick<User, "id"> & UserNameAndImageFragment) | null;
	mediaAttachments: ArrayPick<
		Post["mediaAttachments"],
		"id" | "title" | "description" | "isFile" | "fileURL" | "fileKey" | "fileContentType" | "linkURL"
	>;
	socialReactions: SocialReactionComponents.ReactionsViewProps["reactions"];
	sharingRecord: SimpleSharingViewProps["sharingRecord"] | null;
};

export type TimelineItemProps = {
	announcement: AnnouncementInTimelineItem | null;
	isLoading?: boolean;
} & Partial<ClassNameProps & StyleProps>;

export function TimelineItem({ announcement, isLoading = false, className, style }: TimelineItemProps) {
	const { isModalOpen, openModal, closeModal } = Modal.useToggle();

	const isTextOnly = announcement?.mediaAttachments.length === 0;
	const viewerReaction = announcement?.socialReactions.find((reaction) => reaction.author?.isCurrent) ?? null;
	const announcementID = announcement?.id;
	const reactionFormValues = React.useMemo(() => ({ announcementID: announcementID ?? null }), [announcementID]);

	const { visualAttachments, fileAttachments, linkAttachments } = React.useMemo(
		() => MediaAttachmentUtilities.splitAndExtractMediaAttachments(announcement?.mediaAttachments, announcement?.body),
		[announcement],
	);

	const { isMediaAttachmentModalOpen, onClickMediaAttachment, closeMediaAttachmentModal, mediaAttachmentModalIndex } =
		MediaAttachmentComponents.MediaAttachmentModal.useModal();

	if (!announcement && isLoading) {
		return (
			<div className={makeClassName(styles["timeline-item"], className)} style={style}>
				<Column justify="spaced-start">
					<ModelComponents.AuthorTimestampViewLoadingPlaceholder withTimestamp />
					<TextBlockLoadingPlaceholder style={{ maxWidth: "40rem" }} />
					<Row justify="space-between">
						<TextLineLoadingPlaceholder>Be the first to like this</TextLineLoadingPlaceholder>
						<TextLineLoadingPlaceholder style={{ marginLeft: "0.5rem" }}>View</TextLineLoadingPlaceholder>
					</Row>
				</Column>
			</div>
		);
	}
	if (!announcement) {
		return null;
	}

	return (
		<div
			className={makeClassName(
				styles["timeline-item"],
				isTextOnly ? styles["timeline-item--text-only"] : "",
				className,
			)}
			style={style}
		>
			<Column justify="spaced-start">
				<Row justify="space-between">
					<Row justify="spaced-start" style={{ width: "100%", overflow: "hidden", marginRight: "1rem" }}>
						<AuthorTimestampView
							author={announcement.author}
							timestamp={announcement.startTime}
							style={{ width: "fit-content", maxWidth: "100%", overflow: "hidden" }}
						/>
						<Badge color="#fff" backgroundColor="var(--primary-color)" size="small" style={{ height: "fit-content" }}>
							<Row justify="spaced-start" align="center" horizontalSpacing="0.25rem">
								<Icon.Bell size={12} /> Announcement
							</Row>
						</Badge>
					</Row>

					<SocialReactionComponents.SocialReactionToggleButton
						instanceID={viewerReaction?.id}
						formValues={reactionFormValues}
						iconSize={24}
					/>
				</Row>
				<If condition={announcement.body !== ""}>
					<ModelComponents.TruncatedTextWithGeneralButton
						lines={3}
						onClick={openModal}
						className={styles["timeline-item__body-text"]}
					>
						{announcement.body}
					</ModelComponents.TruncatedTextWithGeneralButton>
				</If>
				{!!announcement.sharingRecord && <SimpleSharingView sharingRecord={announcement.sharingRecord} size="small" />}
				<If condition={visualAttachments.length > 0}>
					<MediaAttachmentComponents.VisualAttachmentsRow
						attachments={visualAttachments}
						onClickAttachment={onClickMediaAttachment}
					/>

					<Modal.If condition={isMediaAttachmentModalOpen}>
						<MediaAttachmentComponents.MediaAttachmentModal
							mediaAttachments={visualAttachments}
							initialIndex={mediaAttachmentModalIndex}
							onClose={closeMediaAttachmentModal}
						/>
					</Modal.If>
				</If>
				<If condition={fileAttachments.length > 0}>
					<MediaAttachmentComponents.FileAttachmentsRow attachments={fileAttachments} />
				</If>
				<If condition={linkAttachments.length > 0}>
					<MediaAttachmentComponents.LinkAttachmentsRow attachments={linkAttachments} />
				</If>

				<Row justify="space-between">
					<SocialReactionComponents.ReactionsView
						announcementID={announcement.id}
						reactions={announcement.socialReactions}
					/>

					<Button variant="link" size="small" onClick={openModal}>
						View
					</Button>
				</Row>
			</Column>

			<Modal.If condition={isModalOpen}>
				<AnnouncementModal announcement={announcement} onClose={closeModal} />
			</Modal.If>
		</div>
	);
}
